import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Globe2, MessageSquare, Loader2, PanelLeft, Users } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { useWorkspace } from '@/features/workspace/workspace-provider'
import { ChatInput } from '@/features/chat/chat-input'
import { MessageList } from '@/features/chat/message-list'
import { SessionList } from '@/features/chat/session-list'
import {
  chatKeys,
  useChatMessages,
  useChatSessions,
  useCreateSession,
  useSendMessage,
  useUpdateSession,
  useUpdateSessionBrandSelection,
} from '@/features/chat/use-chat'
import {
  persistSlashAssistantStub,
  persistSlashUserMessage,
  type ChatMessage,
  type ChatSession,
} from '@/features/chat/chat-api'
import { useInstalledSkills } from '@/features/skills/use-skills'
import { useResearch } from '@/features/research/use-research'
import { ResearchBubble } from '@/features/research/research-bubble'
import { useWikiInvoke } from '@/features/wiki/use-wiki'
import { WikiBubble } from '@/features/wiki/wiki-bubble'
import { useOutput } from '@/features/output/use-output'
import { OutputBubble } from '@/features/output/output-bubble'
import type { PlatformSkill } from '@/features/output/output-api'
import { useImage } from '@/features/image/use-image'
import { ImageBubble } from '@/features/image/image-bubble'
import { markRunStubPersisted, useRunStore } from '@/features/runs/run-store'
import type { SlashCommandKind } from '@mindkeep/shared'
import { useLocalStorage } from '@/lib/use-local-storage'
import { useBrandProfiles } from '@/features/settings/use-brand'
import type { BrandProfile } from '@/features/settings/brand-api'
import { useVaultFiles } from '@/features/vault/use-vault'

export function ChatPage() {
  const { workspace } = useWorkspace()
  const qc = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const activeId = searchParams.get('session')
  const prefill = searchParams.get('prefill') ?? undefined

  const sessionsQuery = useChatSessions()
  const createSession = useCreateSession()
  const updateSession = useUpdateSession()
  const updateBrandSelection = useUpdateSessionBrandSelection()
  const [selectedModel, setSelectedModel] = useState<string | null>(null)
  // Inhalt eines verworfenen Fehlsendungs-Banners zurueck ins Eingabefeld.
  const [restoredDraft, setRestoredDraft] = useState<string | undefined>(undefined)
  const messagesQuery = useChatMessages(activeId)
  const { send, invokeSkill, isStreaming, streamingContent, failedSend, retryFailedSend, dismissFailedSend } =
    useSendMessage(activeId)
  const installedSkillsQuery = useInstalledSkills()
  const brandProfilesQuery = useBrandProfiles()
  const research = useResearch()
  const wiki = useWikiInvoke()
  const output = useOutput()
  const image = useImage()
  const [useWebsiteStyleOverride, setUseWebsiteStyleOverride] = useState(false)
  const [websiteStyleUrl, setWebsiteStyleUrl] = useState('')
  // M5-B: Chat-Liste auf Mobile hinter einem Sheet-Toggle (Desktop unveraendert).
  const [sessionSheetOpen, setSessionSheetOpen] = useState(false)
  const sessionSheetOpenerRef = useRef<HTMLButtonElement>(null)
  const sessionConfirmationOpenRef = useRef(false)
  const chatPanelRef = useRef<HTMLElement>(null)

  useEffect(() => {
    const desktop = window.matchMedia('(min-width: 1024px)')
    const closeDesktopSheet = () => { if (desktop.matches) setSessionSheetOpen(false) }
    closeDesktopSheet()
    desktop.addEventListener('change', closeDesktopSheet)
    return () => desktop.removeEventListener('change', closeDesktopSheet)
  }, [])

  const sessions = sessionsQuery.data?.sessions ?? []
  const messages = messagesQuery.data?.messages ?? []
  const installedSkills = installedSkillsQuery.data?.skills ?? []
  const brandProfiles = brandProfilesQuery.data ?? []

  // B1.6.3 — Letzte aktive Session ueber Route-Wechsel hinaus persistieren.
  const [lastActiveId, setLastActiveId] = useLocalStorage<string | null>(
    'mindkeep:last-active-session',
    null,
  )

  // activeId aus URL → localStorage spiegeln (nur wenn vorhanden)
  useEffect(() => {
    if (activeId) setLastActiveId(activeId)
  }, [activeId, setLastActiveId])

  // Auto-Select bei Mount/Refocus: zuerst lastActiveId (falls noch in Liste),
  // sonst sessions[0]. Sonst nichts (Empty-State).
  useEffect(() => {
    if (activeId) return
    if (sessions.length === 0) return
    const stillExists = lastActiveId && sessions.some((s) => s.id === lastActiveId)
    const candidate = stillExists ? lastActiveId : sessions[0]?.id
    if (candidate) {
      const next = new URLSearchParams(searchParams)
      next.set('session', candidate)
      setSearchParams(next, { replace: true })
    }
  }, [activeId, lastActiveId, searchParams, sessions, setSearchParams])

  // M2-B Run-Survival: Slash-Runs (research/wiki/output/image) leben im globalen
  // run-store und werden bei Session-Wechsel/Navigation NICHT mehr abgebrochen.
  // Sichtbarkeit der Banner ist pro Session gekeyt (slice.sessionId === activeId),
  // Stub-Persistierung dedupt ueber lastStubId im Store (siehe Watcher unten).

  // Helper: Optimistic insert in den Message-Cache.
  const upsertMessageInCache = (sessionId: string, message: ChatMessage) => {
    qc.setQueryData<{ messages: ChatMessage[] }>(chatKeys.messages(sessionId), (prev) => {
      const list = prev?.messages ?? []
      if (list.some((m) => m.id === message.id)) return prev
      return { messages: [...list, message] }
    })
  }

  // Wrapper fuer User-Message-Persistierung vor jedem Slash-Command.
  const persistUserBubble = async (
    sessionId: string,
    rawCommand: string,
    kind: SlashCommandKind,
  ) => {
    try {
      const { message } = await persistSlashUserMessage(sessionId, rawCommand, kind)
      upsertMessageInCache(sessionId, message)
    } catch (err) {
      console.error('persistSlashUserMessage failed', err)
    }
  }

  const persistAssistantBubble = async (
    sessionId: string,
    payload: Parameters<typeof persistSlashAssistantStub>[1],
  ) => {
    try {
      const { message } = await persistSlashAssistantStub(sessionId, payload)
      upsertMessageInCache(sessionId, message)
    } catch (err) {
      console.error('persistSlashAssistantStub failed', err)
    }
  }

  // Watcher: Platform output done/error → Assistant-Stub.
  // Dedup ueber lastStubId im globalen run-store (synchron via getState gelesen,
  // damit StrictMode-Doppel-Effekte und Remounts nicht doppelt persistieren).
  useEffect(() => {
    const s = output.state
    const targetSession = output.sessionId ?? activeId
    if (!targetSession) return
    if (s.status === 'done' && s.result) {
      const id = s.result.vault_id
      if (useRunStore.getState().output.lastStubId === id) return
      markRunStubPersisted('output', id)
      const command = s.platform ?? 'linkedin'
      const content =
        command === 'presentation' && s.jsonPayload
          ? formatPresentationAssistantContent(s.jsonPayload, s.result.vault_path)
          : `/${command} erstellt: [[${s.result.vault_path}]]`
      void persistAssistantBubble(targetSession, {
        content,
        slash_command: command,
        slash_status: 'completed',
        vault_path: s.result.vault_path,
      })
    } else if (s.status === 'error' && s.error) {
      const id = `err:${s.error}`
      if (useRunStore.getState().output.lastStubId === id) return
      markRunStubPersisted('output', id)
      const command = s.platform ?? 'linkedin'
      void persistAssistantBubble(targetSession, {
        content: `/${command} fehlgeschlagen: ${s.error}`,
        slash_command: command,
        slash_status: 'failed',
        error: s.error,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [output.state.status, output.state.result?.vault_id, output.state.error, output.sessionId, activeId])

  // Watcher: Image done/error — Backend image-gen persistiert User+Assistant-
  // Messages (mit Attachment) bereits selbst, also nur Messages-Cache invalidieren
  // damit das frisch persistierte Bild inkl. signed_url im UI auftaucht (B1.5.1).
  useEffect(() => {
    const s = image.state
    const targetSession = image.sessionId ?? activeId
    if (!targetSession) return
    if (s.status === 'done' && s.result) {
      const id = s.result.vault_id
      if (useRunStore.getState().image.lastStubId === id) return
      markRunStubPersisted('image', id)
      void qc.invalidateQueries({ queryKey: chatKeys.messages(targetSession) })
    } else if (s.status === 'error' && s.error) {
      const id = `err:${s.error}`
      if (useRunStore.getState().image.lastStubId === id) return
      markRunStubPersisted('image', id)
      // Fehler-Stub nur via Watcher — backend image-gen schreibt ihn nicht.
      void persistAssistantBubble(targetSession, {
        content: `Bild-Generierung fehlgeschlagen: ${s.error}`,
        slash_command: 'image',
        slash_status: 'failed',
        error: s.error,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [image.state.status, image.state.result?.vault_id, image.state.error, image.sessionId, activeId])

  // Watcher: Wiki done/error → Assistant-Stub
  useEffect(() => {
    const s = wiki.state
    const targetSession = wiki.sessionId ?? activeId
    if (!targetSession) return
    if (s.status === 'done' && !s.error) {
      const firstPath = s.pages[0]?.path ?? s.lintReport ?? `wiki:${s.operation}:${Date.now()}`
      if (useRunStore.getState().wiki.lastStubId === firstPath) return
      markRunStubPersisted('wiki', firstPath)
      const content = s.lintReport
        ? `Lint-Report: [[${s.lintReport}]]`
        : s.pages.length > 0
          ? `Memory-Topic aktualisiert: [[${s.pages[0]!.path}]] (${s.pages.length} Page(s))`
          : s.answer
            ? `Memory-Antwort: ${s.answer.slice(0, 200)}`
            : `Memory-Operation "${s.operation}" abgeschlossen.`
      void persistAssistantBubble(targetSession, {
        content,
        slash_command: 'wiki',
        slash_status: 'completed',
        vault_path: s.pages[0]?.path ?? s.lintReport,
      })
    } else if (s.status === 'error' && s.error) {
      const id = `err:${s.error}`
      if (useRunStore.getState().wiki.lastStubId === id) return
      markRunStubPersisted('wiki', id)
      void persistAssistantBubble(targetSession, {
        content: `Memory-Operation fehlgeschlagen: ${s.error}`,
        slash_command: 'wiki',
        slash_status: 'failed',
        error: s.error,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wiki.state.status, wiki.state.pages.length, wiki.state.error, wiki.sessionId, activeId])

  // Watcher: Research done/error → Assistant-Stub
  useEffect(() => {
    if (!research.state) return
    const s = research.state
    const targetSession = research.sessionId ?? activeId
    if (!targetSession) return
    if (s.phase === 'done' && s.result) {
      const id = s.result.dossier_id
      if (useRunStore.getState().research.lastStubId === id) return
      markRunStubPersisted('research', id)
      void persistAssistantBubble(targetSession, {
        content: `Dossier erstellt: [[${s.result.dossier_path}]]`,
        slash_command: 'research',
        slash_status: 'completed',
        vault_path: s.result.dossier_path,
      })
    } else if (s.phase === 'error' && s.error) {
      const id = `err:${s.error}`
      if (useRunStore.getState().research.lastStubId === id) return
      markRunStubPersisted('research', id)
      void persistAssistantBubble(targetSession, {
        content: `Research fehlgeschlagen: ${s.error}`,
        slash_command: 'research',
        slash_status: 'failed',
        error: s.error,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [research.state?.phase, research.state?.result?.dossier_id, research.state?.error, research.sessionId, activeId])

  const activeSession = useMemo(
    () => sessions.find((s) => s.id === activeId) ?? null,
    [sessions, activeId],
  )

  const handleBrandSelectionChange = (
    primaryBrandId: string | null,
    secondaryBrandIds: string[],
  ) => {
    if (!activeId) return
    updateBrandSelection.mutate({
      sessionId: activeId,
      selection: {
        primary_brand_id: primaryBrandId,
        secondary_brand_ids: secondaryBrandIds.slice(0, 3),
      },
    })
  }

  const handleCreate = async (opts?: { model?: string; keepPrefill?: boolean }) => {
    const session = await createSession.mutateAsync({
      model: opts?.model ?? selectedModel ?? undefined,
    })
    const next = new URLSearchParams()
    next.set('session', session.id)
    if (opts?.keepPrefill && prefill) next.set('prefill', prefill)
    setSearchParams(next)
  }

  const handleModelChange = useCallback(
    (nextModel: string) => {
      const previousModel = selectedModel ?? activeSession?.model ?? workspace.default_model
      setSelectedModel(nextModel)
      if (activeId) {
        updateSession.mutate(
          { sessionId: activeId, patch: { model: nextModel } },
          {
            onError: () => setSelectedModel(previousModel),
          },
        )
      }
    },
    [activeId, selectedModel, activeSession?.model, workspace.default_model, updateSession],
  )

  // Session-Wechsel verwirft einen zurueckgelegten Entwurf (sonst fuellt er
  // beim Navigieren erneut das Eingabefeld).
  useEffect(() => {
    setRestoredDraft(undefined)
  }, [activeId])

  const handleSelect = (id: string) => {
    setSearchParams({ session: id })
  }

  const model = selectedModel ?? activeSession?.model ?? workspace.default_model

  useEffect(() => {
    setSelectedModel(activeSession?.model ?? null)
  }, [activeSession?.model])

  // Wenn ein Prefill vorhanden ist, aber noch keine Session aktiv, automatisch
  // eine neue Session anlegen, damit "Im Chat oeffnen" von anderen Seiten sofort
  // funktioniert (M3).
  useEffect(() => {
    if (!prefill) return
    if (activeId) return
    if (createSession.isPending) return
    if (sessionsQuery.isLoading) return
    if (sessions.length > 0) return
    void handleCreate({ keepPrefill: true })
  }, [prefill, activeId, createSession.isPending, sessionsQuery.isLoading, sessions.length])

  return (
    <div className="flex h-full">
      {/* Desktop: fixe Sidebar. Mobile: Session-Liste im Sheet hinter Toggle. */}
      <div className="hidden lg:flex lg:w-[280px] lg:shrink-0">
        <SessionList
          sessions={sessions}
          activeId={activeId}
          isLoading={sessionsQuery.isLoading}
          isCreating={createSession.isPending}
          onSelect={handleSelect}
          onCreate={handleCreate}
        />
      </div>
      <Sheet
        open={sessionSheetOpen}
        onOpenChange={(next) => {
          if (!next && sessionConfirmationOpenRef.current) return
          setSessionSheetOpen(next)
        }}
      >
        <SheetContent
          side="left"
          className="w-[280px] max-w-[85vw] p-0"
          onCloseAutoFocus={(event) => {
            event.preventDefault()
            const opener = sessionSheetOpenerRef.current
            if (opener?.isConnected && opener.getClientRects().length > 0) opener.focus()
            else chatPanelRef.current?.focus()
          }}
        >
          <SheetHeader className="sr-only">
            <SheetTitle>Chats</SheetTitle>
          </SheetHeader>
          <SessionList
            inSheet
            onConfirmationOpenChange={(open) => { sessionConfirmationOpenRef.current = open }}
            sessions={sessions}
            activeId={activeId}
            isLoading={sessionsQuery.isLoading}
            isCreating={createSession.isPending}
            onSelect={(id) => {
              handleSelect(id)
              setSessionSheetOpen(false)
            }}
            onCreate={() => {
              setSessionSheetOpen(false)
              void handleCreate()
            }}
          />
        </SheetContent>
      </Sheet>

      <main ref={chatPanelRef} aria-label="Fragen" tabIndex={-1} className="flex flex-1 flex-col min-w-0">
        <div className="flex items-center gap-2 border-b border-border px-4 py-2 lg:hidden">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-11 shrink-0 gap-1.5 px-2"
            onClick={() => setSessionSheetOpen(true)}
            ref={sessionSheetOpenerRef}
            aria-label="Chat-Liste öffnen"
          >
            <PanelLeft className="h-4 w-4" />
            Chats
          </Button>
          {activeId && activeSession && (
            <span className="min-w-0 truncate text-sm text-muted-foreground" title={activeSession.title ?? undefined}>
              {activeSession.title?.trim() ? activeSession.title : 'Neuer Chat'}
            </span>
          )}
        </div>
        {!activeId ? (
          <EmptyState
            onCreate={handleCreate}
            onPrefillSelect={(question) => setSearchParams({ prefill: question })}
            isCreating={createSession.isPending}
          />
        ) : messagesQuery.isLoading ? (
          <div className="flex flex-1 items-center justify-center text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : (
          <>
            <div className="flex flex-1 flex-col overflow-hidden">
              <MessageList
                messages={messages}
                streamingContent={streamingContent}
                isStreaming={isStreaming}
              />
              {research.state && research.sessionId === activeId && (
                <div className="border-t border-border bg-muted/30 px-4 py-3 min-[761px]:px-6">
                  <div className="mx-auto max-w-[720px]">
                    <ResearchBubble state={research.state} onDismiss={research.reset} />
                  </div>
                </div>
              )}
              {wiki.state.status !== 'idle' && wiki.sessionId === activeId && (
                <div className="border-t border-border bg-muted/30 px-4 py-3 min-[761px]:px-6">
                  <div className="mx-auto max-w-[720px]">
                    <WikiBubble state={wiki.state} onDismiss={wiki.reset} />
                  </div>
                </div>
              )}
              {output.state.status !== 'idle' && output.sessionId === activeId && (
                <div className="border-t border-border bg-muted/30 px-4 py-3 min-[761px]:px-6">
                  <div className="mx-auto max-w-[720px]">
                    <OutputBubble state={output.state} onDismiss={output.reset} />
                  </div>
                </div>
              )}
              {image.state.status !== 'idle' && image.sessionId === activeId && (
                <div className="border-t border-border bg-muted/30 px-4 py-3 min-[761px]:px-6">
                  <div className="mx-auto max-w-[720px]">
                    <ImageBubble state={image.state} onDismiss={image.reset} />
                  </div>
                </div>
              )}
            </div>
            {failedSend && (
              <div
                data-testid="send-error"
                className="border-t border-destructive/30 bg-destructive/5 px-4 py-3 min-[761px]:px-6"
              >
                <div className="mx-auto flex max-w-[720px] flex-wrap items-center gap-3">
                  <AlertTriangle className="h-4 w-4 shrink-0 text-destructive" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-destructive">
                      Nachricht konnte nicht gesendet werden.
                    </p>
                    <p className="truncate text-xs text-muted-foreground" title={failedSend.content}>
                      {failedSend.content}
                    </p>
                    <p className="text-xs text-muted-foreground">{failedSend.message}</p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => void retryFailedSend()}
                    disabled={isStreaming}
                  >
                    Erneut senden
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      // Verwerfen stellt den Text zurueck ins Eingabefeld,
                      // statt ihn unwiderruflich zu loeschen.
                      if (failedSend.content) setRestoredDraft(failedSend.content)
                      dismissFailedSend()
                    }}
                  >
                    Verwerfen
                  </Button>
                </div>
              </div>
            )}
            <SessionBrandBar
              activeSession={activeSession}
              profiles={brandProfiles}
              isSaving={updateBrandSelection.isPending}
              onChange={handleBrandSelectionChange}
              useWebsiteStyleOverride={useWebsiteStyleOverride}
              websiteStyleUrl={websiteStyleUrl}
              onWebsiteStyleOverrideChange={setUseWebsiteStyleOverride}
              onWebsiteStyleUrlChange={setWebsiteStyleUrl}
            />
            <ChatInput
              model={model}
              disabled={!activeId}
              isStreaming={
                isStreaming ||
                research.isRunning ||
                wiki.state.isRunning ||
                output.state.isRunning ||
                image.state.isRunning
              }
              sessionId={activeId}
              installedSkills={installedSkills}
              prefill={restoredDraft ?? prefill}
              isUpdatingModel={updateSession.isPending}
              allowedModels={workspace.allowed_chat_models}
              onModelChange={handleModelChange}
              onSend={(content, attachments, options) => void send(content, attachments, options)}
              onInvokeSkill={(invocation) => void invokeSkill(invocation)}
              onResearch={(invocation, rawCommand) => {
                if (activeId) void persistUserBubble(activeId, rawCommand, 'research')
                research.start(invocation.topic, activeId, invocation.discovery)
              }}
              onWiki={(invocation, rawCommand) => {
                if (activeId) void persistUserBubble(activeId, rawCommand, 'wiki')
                void wiki.invoke({ ...invocation, sessionId: activeId ?? undefined })
              }}
              onPlatformOutput={(invocation, rawCommand) => {
                if (activeId) void persistUserBubble(activeId, rawCommand, invocation.platform)
                void output.start({
                  platform: invocation.platform as PlatformSkill,
                  dossier: invocation.dossier,
                  auftrag: invocation.auftrag,
                  mode: invocation.mode,
                  topicFreshness: invocation.topicFreshness,
                  presentationFormat: invocation.presentationFormat,
                  useWebsiteStyleOverride:
                    invocation.platform === 'presentation' && useWebsiteStyleOverride && websiteStyleUrl.trim().length > 0,
                  websiteStyleUrl:
                    invocation.platform === 'presentation' && useWebsiteStyleOverride
                      ? websiteStyleUrl.trim()
                      : undefined,
                  sessionId: activeId ?? undefined,
                  primaryBrandId: activeSession?.primary_brand_id ?? undefined,
                  secondaryBrandIds: activeSession?.secondary_brand_ids ?? [],
                })
              }}
              onImage={(invocation, rawCommand) => {
                // Backend image-gen persistiert User-Bubble selbst (B1.5.1) — kein
                // persistUserBubble nötig. rawCommand wird trotzdem konsumiert via
                // backend prompt-Reconstruction.
                void rawCommand
                void image.start({
                  prompt: invocation.prompt,
                  style: invocation.style,
                  primaryBrandId: activeSession?.primary_brand_id ?? undefined,
                  sessionId: activeId ?? undefined,
                })
              }}
            />
          </>
        )}
      </main>
    </div>
  )
}

function formatPresentationAssistantContent(payload: unknown, fallbackVaultPath?: string): string {
  const htmlPath = isRecord(payload)
    ? readString(payload, 'html_vault_path') ?? derivePresentationPath(fallbackVaultPath, 'html')
    : derivePresentationPath(fallbackVaultPath, 'html')
  const pdfPath = isRecord(payload)
    ? readString(payload, 'pdf_vault_path') ?? derivePresentationPath(fallbackVaultPath, 'pdf')
    : derivePresentationPath(fallbackVaultPath, 'pdf')

  if (!htmlPath) return '/presentation erstellt'
  const payloadBlock = isRecord(payload)
    ? compactPresentationPayloadForMessage(payload)
    : null
  const links = `/presentation erstellt: [[${htmlPath}]]${pdfPath ? ` · PDF: [[${pdfPath}]]` : ''}`
  return payloadBlock ? `${links}\n\n\`\`\`mindkeep-presentation\n${JSON.stringify(payloadBlock)}\n\`\`\`` : links
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value)
}

function readString(obj: Record<string, unknown>, key: string): string | undefined {
  const value = obj[key]
  return typeof value === 'string' && value.trim() ? value : undefined
}

function derivePresentationPath(path: string | undefined, ext: 'html' | 'pdf'): string | undefined {
  if (!path) return undefined
  if (path.endsWith(`.${ext}`)) return path
  if (path.endsWith('-manifest.json')) return `${path.slice(0, -'-manifest.json'.length)}.${ext}`
  return path.replace(/\.(md|json|html|pdf)$/i, `.${ext}`)
}

function compactPresentationPayloadForMessage(payload: Record<string, unknown>): Record<string, unknown> | null {
  const compact = {
    html_url: readString(payload, 'html_url'),
    pdf_url: readString(payload, 'pdf_url'),
    html_vault_path: readString(payload, 'html_vault_path'),
    pdf_vault_path: readString(payload, 'pdf_vault_path'),
    aspect: readString(payload, 'aspect'),
    slide_count: typeof payload.slide_count === 'number' ? payload.slide_count : undefined,
    raw_markdown: readString(payload, 'raw_markdown')?.slice(0, 5200),
  }
  return Object.fromEntries(Object.entries(compact).filter(([, value]) => value !== undefined))
}

function SessionBrandBar({
  activeSession,
  profiles,
  isSaving,
  onChange,
  useWebsiteStyleOverride,
  websiteStyleUrl,
  onWebsiteStyleOverrideChange,
  onWebsiteStyleUrlChange,
}: {
  activeSession: ChatSession | null
  profiles: BrandProfile[]
  isSaving: boolean
  onChange: (primaryBrandId: string | null, secondaryBrandIds: string[]) => void
  useWebsiteStyleOverride: boolean
  websiteStyleUrl: string
  onWebsiteStyleOverrideChange: (value: boolean) => void
  onWebsiteStyleUrlChange: (value: string) => void
}) {
  if (!activeSession || profiles.length === 0) return null

  const defaultBrand = profiles.find((p) => p.is_default) ?? profiles[0] ?? null
  const selectedPrimary = activeSession.primary_brand_id ?? defaultBrand?.id ?? ''
  const selectedSecondary = activeSession.secondary_brand_ids ?? []
  const activeBrand = profiles.find((p) => p.id === selectedPrimary) ?? defaultBrand
  const selectableSecondary = profiles.filter((p) => p.id !== selectedPrimary)

  const toggleSecondary = (brandId: string) => {
    const next = selectedSecondary.includes(brandId)
      ? selectedSecondary.filter((id) => id !== brandId)
      : [...selectedSecondary, brandId].slice(0, 3)
    onChange(selectedPrimary || defaultBrand?.id || null, next)
  }

  return (
    <div className="border-t border-border bg-background px-4 py-2 min-[761px]:px-6">
      <div className="mx-auto flex max-w-[720px] flex-wrap items-center gap-3 text-xs">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Users className="h-4 w-4" />
          <span>
            Brand: {activeBrand ? activeBrand.name : 'System'}{activeBrand?.is_default ? ' (Standard)' : ''}
          </span>
        </div>
        <select
          value={selectedPrimary}
          disabled={isSaving}
          onChange={(e) => {
            const primary = e.target.value || defaultBrand?.id || null
            const secondary = selectedSecondary.filter((id) => id !== primary)
            onChange(primary, secondary)
          }}
          className="h-8 rounded-md border border-input bg-background px-2 text-xs"
        >
          {profiles.map((profile) => (
            <option key={profile.id} value={profile.id}>
              {profile.name} ({profile.brand_type}){profile.is_default ? ' – Standard' : ''}
            </option>
          ))}
        </select>
        <div className="flex flex-wrap items-center gap-1">
          {selectableSecondary.map((profile) => {
            const selected = selectedSecondary.includes(profile.id)
            return (
              <button
                key={profile.id}
                type="button"
                disabled={isSaving || (!selected && selectedSecondary.length >= 3)}
                onClick={() => toggleSecondary(profile.id)}
                className={`rounded-md border px-2 py-1 text-xs ${
                  selected
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border text-muted-foreground hover:bg-muted'
                } disabled:cursor-not-allowed disabled:opacity-50`}
                title="Co-Speaker umschalten"
              >
                {profile.name}
              </button>
            )
          })}
        </div>
        <div className="ml-auto flex min-w-[260px] flex-wrap items-center gap-2">
          <label
            className="flex items-center gap-2 text-muted-foreground"
            title="Website einmalig als Stil-Vorlage für /presentation analysieren"
          >
            <input
              type="checkbox"
              checked={useWebsiteStyleOverride}
              onChange={(e) => onWebsiteStyleOverrideChange(e.target.checked)}
              className="h-4 w-4"
            />
            <Globe2 className="h-4 w-4" />
            <span>Website-Stil</span>
          </label>
          <input
            type="url"
            value={websiteStyleUrl}
            onChange={(e) => onWebsiteStyleUrlChange(e.target.value)}
            disabled={!useWebsiteStyleOverride}
            placeholder="firmenwebsite.ch"
            className="h-8 w-[210px] rounded-md border border-input bg-background px-2 text-xs disabled:opacity-50"
          />
        </div>
        {isSaving && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
      </div>
    </div>
  )
}

// M8.1: Beispiel-Fragen, wenn der Demo-Seed (_Beispiele/) im Vault liegt.
// Klick setzt den ?prefill=-Param — die Auto-Create-Logik oben legt dann die
// Session an und fuellt die Frage ins Eingabefeld (gleicher Mechanismus wie
// "Im Chat oeffnen" in Automatisierungen, NICHT auto-gesendet).
const DEMO_EXAMPLE_QUESTIONS = [
  'Was übernimmt die KI laut CEO-Guide — und was bleibt Aufgabe des CEO?',
  'Was ist das Produktivitäts-Paradox aus dem Culture Compass 2026?',
  'Wie funktioniert das Protokoll einer Fallbesprechung?',
]

interface EmptyStateProps {
  onCreate: () => void
  onPrefillSelect: (question: string) => void
  isCreating: boolean
}

function EmptyState({ onCreate, onPrefillSelect, isCreating }: EmptyStateProps) {
  // Render-gated: laeuft nur, solange der Empty-State sichtbar ist (!activeId).
  const { data: vaultData } = useVaultFiles()
  const hasDemo = (vaultData?.entries ?? []).some((entry) => entry.path.startsWith('_Beispiele/'))

  return (
    <div className="flex flex-1 items-center justify-center p-8">
      <div className="max-w-sm space-y-4 text-center">
        <div className="inline-flex h-12 w-12 items-center justify-center rounded-lg bg-muted text-muted-foreground">
          <MessageSquare className="h-6 w-6" />
        </div>
        <h1 className="text-lg font-semibold">Noch keine Konversation gewählt</h1>
        <p className="text-sm text-muted-foreground">
          Starte einen neuen Chat, um mit deinem Wissens-Agent zu sprechen.
        </p>
        {hasDemo ? (
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">Oder probiere eine Beispiel-Frage:</p>
            <div className="flex flex-col gap-2">
              {DEMO_EXAMPLE_QUESTIONS.map((question) => (
                <button
                  key={question}
                  type="button"
                  data-testid="demo-question-chip"
                  disabled={isCreating}
                  onClick={() => onPrefillSelect(question)}
                  className="rounded-md border border-border px-3 py-2 text-left text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {question}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            <Link to="/quellen" data-testid="chat-upload-hint" className="text-primary underline">
              Lade deine ersten Dokumente hoch
            </Link>
            , um loszulegen.
          </p>
        )}
        <Button onClick={onCreate} disabled={isCreating}>
          {isCreating ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : null}
          Neuen Chat starten
        </Button>
      </div>
    </div>
  )
}
